import { logger } from "@/lib/logger";

export const GRAPH_PDF_MIN_BYTES = 100;

export const buildGraphPdfContentUrl = (
  driveId: string,
  itemId: string
): string =>
  `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}/content?format=pdf`;

/** Validates PDF magic bytes and minimum size for staged workspace blobs and Graph responses. */
export const isValidPdfBytes = (bytes: Uint8Array, size: number): boolean =>
  size >= GRAPH_PDF_MIN_BYTES &&
  bytes.length >= 4 &&
  bytes[0] === 0x25 &&
  bytes[1] === 0x50 &&
  bytes[2] === 0x44 &&
  bytes[3] === 0x46;

/**
 * Fetches a PDF rendition of a drive item via Microsoft Graph (Word native conversion).
 * Follows Graph 302 redirects to preauthenticated URLs in the browser.
 */
export const fetchGraphPdfBlob = async (
  driveId: string,
  siteId: string,
  sharepointFileId: string,
  token: string
): Promise<Blob | null> => {
  const operation = "fetchGraphPdfBlob";
  const fileDownloadUrl = buildGraphPdfContentUrl(driveId, sharepointFileId);

  try {
    const response = await fetch(fileDownloadUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      logger.warn("Microsoft Graph PDF conversion request failed", {
        operation,
        driveId,
        siteId,
        itemId: sharepointFileId,
        status: response.status,
        statusText: response.statusText,
        errorText: errorText.slice(0, 500),
      });
      return null;
    }

    const blob = await response.blob();
    const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    if (!isValidPdfBytes(header, blob.size)) {
      logger.warn("Graph PDF response is not a valid PDF", {
        operation,
        driveId,
        siteId,
        itemId: sharepointFileId,
        blobSize: blob.size,
        blobType: blob.type,
      });
      return null;
    }

    logger.info("Fetched Microsoft Graph PDF for drive item", {
      operation,
      driveId,
      siteId,
      itemId: sharepointFileId,
      blobSize: blob.size,
    });
    return blob;
  } catch (error) {
    logger.warn("Failed to fetch Microsoft Graph PDF", {
      operation,
      driveId,
      siteId,
      itemId: sharepointFileId,
      error,
    });
    return null;
  }
};
